import { useCallback, useState } from "react"

import type { MimeType } from "@/components/upload/constants"
import { IMAGE_MIME_TYPES } from "@/components/upload/constants"
import type { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFileUpload } from "@/hooks/useFileUpload"

interface UseImageUploadProps {
  referenceTableName: ReferenceTableName
}

type UploadState = Record<FieldGroupEnum, boolean>

interface ImageUploadHook {
  uploading: UploadState
  uploadFile: (file: File, type: FieldGroupEnum, referenceId: string, label?: string) => Promise<boolean>
}

export const useImageUpload = ({ referenceTableName }: UseImageUploadProps): ImageUploadHook => {
  const { uploadFile: baseUploadFile } = useFileUpload()
  const [uploading, setUploading] = useState<UploadState>({} as UploadState)

  const uploadFile = useCallback(
    async (file: File, type: FieldGroupEnum, referenceId: string, label?: string) => {
      if (!IMAGE_MIME_TYPES.includes(file.type as MimeType)) {
        console.error(`Invalid file type: ${file.type}. Allowed types: ${IMAGE_MIME_TYPES.join(", ")}`)
        return false
      }

      if (!referenceId) {
        console.error("No reference ID available")
        return false
      }

      setUploading((prev) => ({ ...prev, [type]: true }))

      try {
        const success = await baseUploadFile({
          file,
          type: "image",
          metadata: {
            fieldGroup: type,
            referenceId,
            referenceTableName,
          },
          label,
        })

        if (!success) {
          console.error("Failed to upload image")
          return false
        }

        return true
      } catch (error) {
        console.error("Failed to upload image:", error)
        return false
      } finally {
        setUploading((prev) => ({ ...prev, [type]: false }))
      }
    },
    [baseUploadFile, referenceTableName]
  )

  return {
    uploading,
    uploadFile,
  }
}
